import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton } from "../../StyledCss/styledbtn";
import PageHeader from "../PageHeader";
import "./index.css";
import { RiPencilLine } from "react-icons/ri";
import { BsFillTrashFill } from "react-icons/bs";
import { AgGridReact } from "ag-grid-react";
import {
  getEmailTemplates,
  deleteEmailTemplate,
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import PopupModal from "../PopupModal";
import toast from "react-hot-toast";
import { SecondaryButton } from "../../StyledCss/styledbtn";
import TemplatePreviewModal from "../AddEmailTemplate/TemplatePreviewModal";
import { Eye } from "lucide-react";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";
const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const parseSignatureHtml = (signatureHtml) => {
  if (!signatureHtml || typeof signatureHtml !== "string") return null;

  const signature = {};

  // Extract image
  const imgMatch = signatureHtml.match(/<img[^>]+src="([^"]+)"/i);
  if (imgMatch) {
    signature.image = imgMatch[1];
  }

  // Extract email from href
  const emailHrefMatch = signatureHtml.match(/mailto:([^"]+)"/i);
  if (emailHrefMatch) {
    signature.email = emailHrefMatch[1];
  }

  // Extract website from href
  const websiteHrefMatch = signatureHtml.match(/<a[^>]+href="(https?:\/\/[^"]+)"/i);
  if (websiteHrefMatch) {
    signature.website = websiteHrefMatch[1].replace(/^https?:\/\/(www\.)?/, "");
  }

  const text = signatureHtml
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim();
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line);

  lines.forEach((line) => {
    if (line.includes("|") && !line.includes("@") && !line.includes("www")) {
      const parts = line
        .split("|")
        .map((s) => s.trim())
        .filter((s) => s);
      if (parts.length >= 2) {
        signature.title = parts[0];
        signature.phone = parts[1];
      } else if (parts.length === 1) {
        signature.title = parts[0];
      }
    } else if (!signature.email && line.includes("@")) {
      const emailMatch = line.match(
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
      );
      if (emailMatch) {
        signature.email = emailMatch[0];
      }
    } else if (!signature.website && line.includes("www.")) {
      signature.website = line.replace(/^www\./, "");
    } else if (
      line &&
      !line.includes("Thanks") &&
      !line.includes("Regards") &&
      !signature.name &&
      lines.indexOf(line) > 0
    ) {
      if (!/^\d+$/.test(line)) {
        signature.name = line;
      }
    }
  });

  return Object.keys(signature).length > 0 ? signature : null;
};

const EmailTemplates = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreateTemplate = hasPermission(FEATURE_CODES.EMAIL_TEMPLATE_CREATE);
  const canEditTemplate = hasPermission(FEATURE_CODES.EMAIL_TEMPLATE_EDIT);
  const canDeleteTemplate = hasPermission(FEATURE_CODES.EMAIL_TEMPLATE_DELETE);
 const [loadingStates, setLoadingStates] = useState({
    deleteEmailTemplate: false,
  });
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.success);
  const [emailTemplatesData, setEmailTemplatesData] = useState([]);
  const [emailTemplateToDelete, setEmailTemplateToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const editEmailTemplate = useCallback(
    (emailTemplate) => {
      navigate("/email_templates/edit_template", {
        state: { emailTemplate, isEditMode: true },
      });
    },
    [navigate],
  );

  const handleAddTemplate = () => {
    navigate("/email_templates/create_template");
  };
  const getEmailTemplateList = useCallback(async () => {
    try {
      setApiStatus(apiStatusConstants.inProgress);
      const response = await getEmailTemplates();

      if (response.ok) {
        const emailTemplatedata = convertKeysToCamelCase(await response.json());
        setEmailTemplatesData(emailTemplatedata.data);
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
      setApiStatus(apiStatusConstants.failure);
    }
  }, []);
  const confirmDeleteEmailTemplate = useCallback(async () => {
    try {
            setLoadingStates({ deleteEmailTemplate: true });

      const response = await deleteEmailTemplate(emailTemplateToDelete.id);
      if (response.status === 200) {
        toast.success("Email Template Deleted Successfully");
        await getEmailTemplateList();
        setIsDeleteModalOpen(false);
        setEmailTemplateToDelete(null);
      } else {
        toast.error("Failed to delete Email Template ");
      }
    } catch (error) {
      console.error("Error deleting Email Template :", error);
      toast.error("Failed to delete Email Template ");
    }
    finally {
      setLoadingStates({ deleteEmailTemplate: false });
    }
  }, [emailTemplateToDelete, getEmailTemplateList]);
  useEffect(() => {
    getEmailTemplateList();
  }, [getEmailTemplateList]);
  const onClickRetry = useCallback(
    () => getEmailTemplateList(),
    [getEmailTemplateList],
  );

  const cancelDeleteEmailTemplate = useCallback(() => {
    setIsDeleteModalOpen(false);
    setEmailTemplateToDelete(null);
  }, []);
  const handleDeleteEmailTemplate = useCallback((emailTemplate) => {
    setEmailTemplateToDelete(emailTemplate);
    setIsDeleteModalOpen(true);
  }, []);

  const handlePreviewTemplate = useCallback((emailTemplate) => {
    const parsedSignature =
      typeof emailTemplate.signature === "string"
        ? parseSignatureHtml(emailTemplate.signature)
        : emailTemplate.signature;
    const transformedTemplate = {
      name: emailTemplate.templateName,
      type: emailTemplate.templateType,
      subject: emailTemplate.subject,
      body: emailTemplate.emailBody,
      signature: parsedSignature,
      company_logo: parsedSignature?.image,
    };
    setPreviewTemplate(transformedTemplate);
    setShowPreview(true);
  }, []);

  const defaultColDef = useMemo(
    () => ({
      cellStyle: { fontSize: "14px" },
      sortable: true,
      filter: true,
      floatingFilter: true,
    }),
    [],
  );
  const columnDefs = useMemo(
    () => [
      {
        headerName: "TEMPLATE NAME",
        field: "templateName",
        width: 400,
        pinned: "left",
        suppressMenu: true
      },
      {
        headerName: "TEMPLATE TYPE",
        field: "templateType",
        width: 300,
        suppressMenu: true
      },
      {
        headerName: "CREATED AT",
        field: "createdAt",
        width: 300,
        suppressMenu: true,
        valueFormatter: (params) => params.value ? params.value.split("T")[0] : "",
      },
      {
        headerName: "CREATED BY",
        field: "createdBy",
        width: 300,
        suppressMenu: true
      },
      {
        headerName: "ACTIONS",
        width: 200,
        cellRenderer: (params) => {
          return (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                height: "100%",
              }}
            >
              {canEditTemplate && (
                <div title="Edit" className="icon-wrapper">
                  <RiPencilLine
                    style={{
                      cursor: "pointer",
                      color: "#28a745",
                      fontSize: "20px",
                    }}
                    onClick={() => editEmailTemplate(params.data)}
                    title="Edit"
                  />
                </div>
              )}

              {canDeleteTemplate && (
                <div
                  title="Delete"
                  className="icon-wrapper"
                  onClick={() => {
                    handleDeleteEmailTemplate(params.data);
                  }}
                >
                  <BsFillTrashFill
                    style={{
                      cursor: "pointer",
                      color: "#dc3545",
                      fontSize: "20px",
                    }}
                  />
                </div>
              )}
              <div
                title="Preview"
                className="icon-wrapper"
                onClick={() => {
                  handlePreviewTemplate(params.data);
                }}
              >
                <Eye
                  style={{
                    cursor: "pointer",
                    color: "#007bff",
                    fontSize: "20px",
                  }}
                />
              </div>
            </div>
          );
        },
        sortable: false,
        filter: false,
        floatingFilter: false,
      },
    ],
    [
      editEmailTemplate,
      handlePreviewTemplate,
      handleDeleteEmailTemplate,
      canEditTemplate,
      canDeleteTemplate,
    ],
  );
  const renderTable = useCallback(
    () => (
      <div
        className="ag-theme-alpine"
        style={{
          height: "78vh",
          fontSize: "14px",
          marginLeft: "20px",
          marginRight: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={emailTemplatesData}
          pagination={true}
          paginationPageSize={10}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableCellTextSelection={true}
        />
      </div>
    ),
    [columnDefs, emailTemplatesData, defaultColDef],
  );
  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={onClickRetry} />;
      default:
        return null;
    }
  };

  return (
    <div>

      <Header />
      <div className="d-flex ">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Email Template</PageHeaderName>

            {canCreateTemplate && (
              <PrimaryButton onClick={handleAddTemplate}>
                Add Template
              </PrimaryButton>
            )}
          </PageHeader>
          <div className="table-container">{renderData()}</div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <div className="no-modal-mb">
        <PopupModal
          showModal={isDeleteModalOpen}
          modalName="Confirm Delete"
          closeBtn={cancelDeleteEmailTemplate}
          marginBottom="0px"
        >
          <div>
            <p>
              Are you sure you want to delete the email template "
              {emailTemplateToDelete?.templateName}"?
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "center",
                marginTop: "15px",
              }}
            >
              <SecondaryButton onClick={cancelDeleteEmailTemplate}>
                No
              </SecondaryButton>
              <PrimaryButton onClick={confirmDeleteEmailTemplate} disabled={loadingStates.deleteEmailTemplate}>
                                {loadingStates.deleteEmailTemplate ? "Deleting..." : "Yes"}

              </PrimaryButton>
            </div>
          </div>
        </PopupModal>
      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        open={showPreview}
        onOpenChange={setShowPreview}
        template={previewTemplate}
      />
    </div>
  );
};

export default EmailTemplates;
